import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Box, Button, Popover, Typography } from "@mui/material";
import { LabelOutlined } from "@mui/icons-material";
import color from "../../../Constants/colors";
import { getDocumentCraftingError } from "../../../Api/documentCrafting";
import {
  DocumentCraftingDocumentDetail,
  DocumentCraftingDocumentLabelsUpdate,
  DocumentCraftingLabelScope,
  LABEL_COLORS,
} from "../../../Models/documentCrafting";
import {
  documentCraftingKeys,
  useCreateDocumentCraftingLabel,
  useDocumentCraftingLabels,
  useSetDocumentCraftingDocumentLabels,
} from "../../../Query/useDocumentCrafting";
import LabelChips from "../Common/LabelChips";
import LabelPicker from "./LabelPicker";

interface Props {
  document: DocumentCraftingDocumentDetail;
}

/**
 * The labels of the document as chips, edited in a popover: project labels
 * by editors, personal labels by everyone who can see the document. Personal
 * documents (no workspace) take no project labels.
 */
const DocumentLabels: React.FC<Props> = ({ document }) => {
  const { t } = useTranslation("documentCrafting");
  const queryClient = useQueryClient();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const labels = useDocumentCraftingLabels(false);
  const setLabels = useSetDocumentCraftingDocumentLabels();
  const createLabel = useCreateDocumentCraftingLabel();
  const canEditProject = document.permissions.can_edit;

  const personalOptions = labels.data?.personal ?? [];
  const hasLabels =
    document.project_labels.length + document.personal_labels.length > 0;

  const save = (data: DocumentCraftingDocumentLabelsUpdate) =>
    setLabels.mutate(
      { documentId: document.id, data },
      {
        onError: (error) => {
          // A label deleted meanwhile (e.g. in another tab): reload the lists.
          if (getDocumentCraftingError(error)?.code === "label_not_found") {
            queryClient.invalidateQueries({
              queryKey: documentCraftingKeys.labels,
            });
            queryClient.invalidateQueries({
              queryKey: documentCraftingKeys.document(document.id),
            });
          }
        },
      },
    );

  const createPersonal = (name: string) =>
    createLabel.mutate(
      {
        scope: DocumentCraftingLabelScope.PERSONAL,
        name,
        color: LABEL_COLORS[personalOptions.length % LABEL_COLORS.length],
      },
      {
        onSuccess: (label) =>
          save({
            personal_label_ids: [
              ...document.personal_labels.map((item) => item.id),
              label.id,
            ],
          }),
      },
    );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 0.75,
        minHeight: 28,
      }}
    >
      {hasLabels ? (
        <LabelChips
          projectLabels={document.project_labels}
          personalLabels={document.personal_labels}
        />
      ) : (
        <Typography sx={{ fontSize: 13, color: color.gray500 }}>
          {t("detail.labels.none")}
        </Typography>
      )}
      <Button
        size="small"
        color="secondary"
        startIcon={<LabelOutlined sx={{ fontSize: 16 }} />}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{ minHeight: 28, py: 0 }}
      >
        {t("detail.labels.edit")}
      </Button>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: { sx: { width: 380, maxWidth: "calc(100vw - 32px)", p: 2 } },
        }}
      >
        <Typography
          sx={{ fontSize: 14, fontWeight: 700, color: color.gray900, mb: 1.5 }}
        >
          {t("detail.labels.title")}
        </Typography>
        <LabelPicker
          projectOptions={document.is_personal ? [] : (labels.data?.project ?? [])}
          personalOptions={personalOptions}
          projectValue={document.project_labels}
          personalValue={document.personal_labels}
          canEditProject={canEditProject}
          loading={labels.isLoading || setLabels.isPending || createLabel.isPending}
          autoFocus
          onChange={save}
          onCreatePersonal={createPersonal}
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
          <Button size="small" onClick={() => setAnchor(null)}>
            {t("detail.labels.done")}
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

export default DocumentLabels;
